import { useCallback, useSyncExternalStore } from 'react';
import { Appearance, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Appearance preference: what the user picked in Settings.
 * `system` defers to the OS. `useTheme` resolves it to a concrete mode so
 * consumers never have to think about the indirection.
 */
export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

/**
 * Storage key. Must stay in step with the pre-paint bootstrap inlined in
 * `apps/gripp-web/public/index.html`, which reads this key before the bundle
 * loads to avoid a flash of the wrong theme. `themeCss.spec.ts` asserts the
 * two agree.
 */
export const THEME_STORAGE_KEY = 'gripp-theme';

/**
 * What the app resolves to when the user has never picked anything.
 *
 * Deliberately `light`, not `system`. Theme *application* cannot be feature
 * flagged: the pre-paint bootstrap in `index.html` runs before the bundle (and
 * therefore before any flag) is loaded, and `ThemeManager` mounts for everyone
 * including the anonymous flows. If absence resolved to `system`, deploying
 * this would hand the dark palette to every OS-dark user while the APPEARANCE
 * flag is still off and Settings -> Appearance is hidden, with no way out.
 *
 * `system` is honoured only once it has been *chosen* in that flag-gated
 * panel, which keeps the flag a real gate without needing flag state
 * pre-paint. Revisit this default once APPEARANCE is on everywhere.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * `Platform.OS` is `'web'` under react-native-web (gripp-web), so this and
 * `isBrowser()` never disagree there. shared-logic's own jest suite stubs
 * `Platform.OS` to `'web'` unconditionally (see `test-stubs/react-native.js`)
 * so it always exercises the browser branch below; the native branch is
 * exercised by `gripp-app`'s own specs, which run under a real `'ios'` /
 * `'android'` `Platform.OS`.
 */
const isNative = (): boolean => Platform.OS !== 'web';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

/**
 * Native's in-memory mirror of the AsyncStorage-backed preference.
 *
 * `useSyncExternalStore`'s snapshot getter must be synchronous, but
 * AsyncStorage is not, so the source of truth for a synchronous read is this
 * module-level mirror: `nativeThemeReady` populates it once from the initial
 * async read, and `writeStoredPreference` updates it optimistically on every
 * write (the AsyncStorage write itself happens in the background). Before the
 * initial read resolves, `readStoredPreference()` returns `undefined` here,
 * which `getPreferenceSnapshot` already treats as "nothing chosen yet" and
 * resolves to `DEFAULT_THEME_PREFERENCE` — i.e. cold start is light, same as
 * an unvisited browser, until the real value loads.
 */
let nativePreferenceMirror: ThemePreference | undefined;

/**
 * Declared here, above `nativeThemeReady`, rather than down by
 * `subscribeToPreference` below: `nativeThemeReady`'s `.then` callback reads
 * this Set, and while a `.then` callback is a microtask - guaranteed to run
 * only after this module finishes evaluating top to bottom, so the forward
 * reference was never actually unsafe - a future refactor that resolves the
 * promise synchronously would turn that into a TDZ error at import. Declaring
 * it first removes the ordering dependency entirely.
 */
const preferenceListeners = new Set<() => void>();

/**
 * Resolves once the initial AsyncStorage read has completed and (if a value
 * was found) the mirror above is populated and subscribers notified. Native's
 * counterpart to web's synchronous pre-paint bootstrap: the app has no
 * blocking-script equivalent, so `apps/gripp-app/src/app/_layout.tsx` awaits
 * this before hiding the native splash screen, to avoid a light-then-dark
 * flash on a device set to dark. Resolves immediately on web and in any
 * non-native environment, where the preference is already known synchronously.
 */
export const nativeThemeReady: Promise<void> = isNative()
  ? AsyncStorage.getItem(THEME_STORAGE_KEY)
      .then((raw) => {
        if (!raw) return;
        // Something else (e.g. the account-preference reconcile that runs on
        // every authenticated load, see `_layout.tsx`'s `AuthenticatedAppBase`)
        // may have already written a fresher choice into the mirror while
        // this read was in flight. Clobbering it with the on-disk value this
        // read started with would silently revert that fresher choice.
        if (nativePreferenceMirror !== undefined) return;
        const parsed: unknown = JSON.parse(raw);
        const theme = (parsed as { theme?: unknown } | null)?.theme;
        if (isPreference(theme)) {
          nativePreferenceMirror = theme;
          preferenceListeners.forEach((notify) => notify());
        }
      })
      .catch(() => undefined) // corrupt or unavailable: stays undefined, same as web
  : Promise.resolve();

/**
 * Reads the stored preference, or `undefined` when the user has never chosen
 * one (or, on native, before the initial async read has resolved). Never
 * throws: storage can be unavailable or corrupt.
 *
 * The absent case is distinct from any concrete value on purpose. Callers that
 * need something to render with fall back to `DEFAULT_THEME_PREFERENCE`;
 * callers deciding whether there is a choice worth persisting (see
 * `useSetDefaultTheme`) need to tell "never picked" from "picked light".
 */
export const readStoredPreference = (): ThemePreference | undefined => {
  if (isNative()) return nativePreferenceMirror;
  if (!isBrowser()) return undefined;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    const theme = (parsed as { theme?: unknown } | null)?.theme;
    return isPreference(theme) ? theme : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Returns false when storage rejected the write (private mode, quota; native
 * has no synchronous equivalent, so it always reports success here and lets
 * the background AsyncStorage write fail silently, same as a corrupt read).
 */
const writeStoredPreference = (theme: ThemePreference): boolean => {
  if (isNative()) {
    nativePreferenceMirror = theme;
    AsyncStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ theme })).catch(
      () => undefined,
    );
    return true;
  }
  if (!isBrowser()) return false;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ theme }));
    return true;
  } catch {
    return false;
  }
};

/**
 * Last-resort in-memory value, used only when storage rejected the write, so
 * the choice still applies for the rest of the session. Deliberately consulted
 * *after* storage, so a successful write always wins and no module state
 * lingers in the normal path.
 */
let unpersistedPreference: ThemePreference | undefined;

const subscribeToPreference = (onChange: () => void): (() => void) => {
  preferenceListeners.add(onChange);
  return () => {
    preferenceListeners.delete(onChange);
  };
};

const getPreferenceSnapshot = (): ThemePreference =>
  readStoredPreference() ?? unpersistedPreference ?? DEFAULT_THEME_PREFERENCE;

const setStoredPreference = (next: ThemePreference): void => {
  unpersistedPreference = writeStoredPreference(next) ? undefined : next;
  preferenceListeners.forEach((notify) => notify());
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

const subscribeToSystemTheme = (onChange: () => void): (() => void) => {
  if (isNative()) {
    const subscription = Appearance.addChangeListener(onChange);
    return () => subscription.remove();
  }
  if (!isBrowser() || !window.matchMedia) return () => undefined;
  const mql = window.matchMedia(DARK_QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
};

const getSystemTheme = (): ResolvedTheme => {
  if (isNative()) return Appearance.getColorScheme() === 'dark' ? 'dark' : 'light';
  return isBrowser() && window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
};

/**
 * Resolved appearance plus a setter.
 *
 * Both halves are read through `useSyncExternalStore`, because both are
 * genuinely external: the OS preference lives behind `matchMedia`, and ours
 * lives in local storage (which the pre-paint bootstrap in `index.html` also
 * reads, before any of this code exists).
 *
 * This deliberately does NOT keep the preference in the react-query cache as
 * `enabled: false` + `initialData` client state. That pattern silently breaks
 * the one consumer that matters: react-query does not notify a *disabled*
 * observer when `setQueryData` runs, so `setQueryData` updated the cache while
 * `ThemeManager` - which sits high in the tree and has no other reason to
 * re-render - kept painting the old mode. The picked theme would then appear
 * only on the next unrelated re-render or a full reload. Verified against
 * react-query v5 in `useTheme.spec.tsx`, which now asserts that an independent
 * subscriber sees the change.
 */
export const useTheme = () => {
  const theme = useSyncExternalStore<ThemePreference>(
    subscribeToPreference,
    getPreferenceSnapshot,
    () => DEFAULT_THEME_PREFERENCE, // SSR snapshot; native never renders server-side
  );

  const systemTheme = useSyncExternalStore<ResolvedTheme>(
    subscribeToSystemTheme,
    getSystemTheme,
    () => 'light', // SSR snapshot; native never renders server-side
  );

  const setTheme = useCallback((next: ThemePreference) => {
    setStoredPreference(next);
  }, []);

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;

  return { theme, resolvedTheme, setTheme };
};
